import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  IconButton,

  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  Close,
  LocationOn,
  Category,
  Sports
} from '@mui/icons-material';
import { setFilters, clearFilters, fetchOrganizations } from '../redux/slices/organisationsSlice';
import './OrganisationFilters.css';

const OrganisationFilters = ({ onClose, isDrawer = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { filters } = useSelector(state => state.organisations);
  
  const [localFilters, setLocalFilters] = useState(filters);
  const [expanded, setExpanded] = useState(!isMobile);

  const organisationTypes = [
    { value: "academie_sportive", label: "Académie sportive" },
    { value: "agence_evenementiel", label: "Agence d’événementiel sportif" },
    { value: "association_sportive", label: "Association sportive" },
    { value: "association_socio_sportive", label: "Association socio-sportive" },
    { value: "autre", label: "Autre (à préciser)" },
    { value: "centre_formation", label: "Centre de formation sportive" },
    { value: "centre_loisirs", label: "Centre de loisirs" },
    { value: "club_amateur", label: "Club amateur" },
    { value: "club_professionnel", label: "Club professionnel" },
    { value: "collectivite_territoriale", label: "Collectivité territoriale" },
    { value: "comite_departemental", label: "Comité départemental" },
    { value: "comite_regional", label: "Comité régional" },
    { value: "complexe_sportif", label: "Complexe sportif" },
    { value: "ecole_primaire", label: "École primaire" },
    { value: "entreprise_privee", label: "Entreprise privée" },
    { value: "enseignement_secondaire", label: "Enseignement secondaire" },
    { value: "enseignement_superieur", label: "Enseignement supérieur" },
    { value: "federation_sportive", label: "Fédération sportive" },
    { value: "fondation", label: "Fondation" },
    { value: "institution_publique", label: "Institution publique" },
    { value: "ligue_sportive", label: "Ligue sportive" },
    { value: "maison_quartier", label: "Maison de quartier" },
    { value: "organisme_formation", label: "Organisme de formation" },
    { value: "organisme_medico_social", label: "Organisme médico-social" },
    { value: "plateforme_digitale", label: "Plateforme digitale" },
    { value: "salle_sport", label: "Salle de sport / fitness" },
    { value: "service_jeunesse", label: "Service jeunesse et sports" },
    { value: "insertion_sport", label: "Structure d’insertion" },
    { value: "handisport", label: "Structure handisport" },
    { value: "sport_adapte", label: "Structure sport adapté" },
    { value: "suaps", label: "Structure universitaire (SUAPS)" },
  ];

  const disciplines = [
    'Tennis',
    'Football',
    'Basketball',
    'Natation',
    'Course à pied',
    'Cyclisme',
    'Fitness',
    'Musculation',
    'Yoga',
    'Pilates',
    'CrossFit',
    'Boxe',
    'Arts martiaux',
    'Volleyball',
    'Handball',
    'Rugby',
    'Golf',
    'Escalade',
    'Ski',
    'Snowboard',
    'Surf',
    'Triathlon',
    'Athlétisme',
    'Gymnastique',
    'Danse',
    'Aquagym',
    'Aquabike'
  ];

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Pour les champs autres que search et location, appliquer immédiatement
    if (field !== 'search' && field !== 'location') {
      const newFilters = {
        ...localFilters,
        [field]: value
      };
      dispatch(setFilters(newFilters));
      dispatch(fetchOrganizations());
    }
  };

  const handleDisciplineToggle = (discipline) => {
    const currentDisciplines = localFilters.disciplines || [];
    const newDisciplines = currentDisciplines.includes(discipline)
      ? currentDisciplines.filter(d => d !== discipline)
      : [...currentDisciplines, discipline];
    
    handleFilterChange('disciplines', newDisciplines);
  };

  const handleApplyFilters = () => {
    dispatch(setFilters(localFilters));
    dispatch(fetchOrganizations());
    if (isDrawer && onClose) {
      onClose();
    }
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      search: '',
      location: '',
      type: '',
      disciplines: [],
      verified: false
    };
    setLocalFilters(emptyFilters);
    dispatch(clearFilters());
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleApplyFilters();
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.location) count++;
    if (localFilters.type) count++;
    if (localFilters.disciplines?.length > 0) count++;
    if (localFilters.verified) count++;
    return count;
  };

  const FilterContent = () => (
    <Box className="organisation-filters">
      {/* Search Section */}
      <Box component="form" onSubmit={handleSearch} className="search-section">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom de l'organisation"
              placeholder="Rechercher une organisation..."
              value={localFilters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                sx: {
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: 'white' }
              }}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Localisation"
              placeholder="Ville, région..."
              value={localFilters.location || ''}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              InputProps={{
                startAdornment: <LocationOn sx={{ color: 'text.secondary', mr: 1 }} />,
                sx: {
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: 'white' }
              }}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
        </Grid>
        
        {!isDrawer && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Search />}
              sx={{ textTransform: 'none' }}
            >
              Rechercher
            </Button>
            {getActiveFiltersCount() > 0 && (
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                sx={{ textTransform: 'none' }}
              >
                Effacer ({getActiveFiltersCount()})
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Advanced Filters */}
      <Accordion 
        expanded={expanded} 
        onChange={() => setExpanded(!expanded)}
        className="filters-accordion"
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList />
            <Typography variant="subtitle1" fontWeight={500} sx={{ color: 'white' }}>
              Filtres avancés
            </Typography>
            {getActiveFiltersCount() > 0 && (
              <Chip 
                label={getActiveFiltersCount()} 
                size="small" 
                color="primary" 
              />
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Type d'organisation */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel sx={{ color: 'white' }}>Type d'organisation</InputLabel>
                <Select
                  value={localFilters.type || ''}
                  label="Type d'organisation"
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  startAdornment={<Category sx={{ color: 'text.secondary', mr: 1 }} />}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    },
                    '& .MuiSelect-icon': {
                      color: 'white'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Tous les types</em>
                  </MenuItem>
                  {organisationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Organisation vérifiée */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localFilters.verified || false}
                    onChange={(e) => handleFilterChange('verified', e.target.checked)}
                    color="primary"
                  />
                }
                label="Organisations vérifiées uniquement"
                sx={{ mt: 1, color: 'white' }}
              />
            </Grid>

            {/* Disciplines */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Sports />
                Disciplines sportives
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {disciplines.map((discipline) => (
                  <Chip
                    key={discipline}
                    label={discipline}
                    onClick={() => handleDisciplineToggle(discipline)}
                    color={localFilters.disciplines?.includes(discipline) ? 'primary' : 'default'}
                    variant={localFilters.disciplines?.includes(discipline) ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ 
                      cursor: 'pointer',
                      color: localFilters.disciplines?.includes(discipline) ? 'white' : 'white',
                      borderColor: localFilters.disciplines?.includes(discipline) ? 'primary.main' : 'white',
                      '&:hover': {
                        backgroundColor: localFilters.disciplines?.includes(discipline) 
                          ? 'primary.dark' 
                          : 'action.hover'
                      }
                    }}
                  />
                ))}
              </Box>
              {localFilters.disciplines?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {localFilters.disciplines.length} discipline(s) sélectionnée(s)
                </Typography>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons for Drawer */}
      {isDrawer && (
        <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearFilters}
            sx={{ textTransform: 'none' }}
          >
            Effacer
          </Button>
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleApplyFilters}
            sx={{ textTransform: 'none' }}
          >
            Appliquer
          </Button>
        </Box>
      )}
    </Box>
  );

  if (isDrawer) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Filtres
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <FilterContent />
      </Box>
    );
  }

  return <FilterContent />;
};

export default OrganisationFilters;